import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'environments/environment';
import { BaseService } from '../../core';
import { PastaIAUsuarioResponse } from './response/pasta-ia-usuario.response';
import { MensagemChatResponse } from './response/mensagem-chat.response';
import { ChatResponse } from './response/chat-sem-pasta.response';
import { AgenteIAResponse } from './response/agente-ia.response';
import { CadastrarChatRequest } from './request/cadastrar-chat.request';
import { EditarPastaRequest } from './request/editar-pasta.request';
import { MoverChatPastaRequest } from './request/mover-chat-pasta.request';
import { CadastrarPastaRequest } from './request/cadastrar-pasta.request';
import { DeletarPastaRequest } from './request/deletar-pasta.request';
import { DeletarChatRequest } from './request/deletar-chat.request';
import { EnviarMensagemRequest } from './request/enviar-mensagem.request';
import { RenomearChatRequest } from './request/renomear-chat.request';

@Injectable({ providedIn: 'root' })
export class ChatIAService extends BaseService {
  private ChatIAEndpoint = `${environment.urlEdducaImobApi}EdducaImobCore/ChatIA/`;
  private loaderAtivo = false;

  constructor(private http: HttpClient) {
    super();
  }

  async obterPastasDoUsuario(usuarioId: number): Promise<PastaIAUsuarioResponse[]> {
    return await firstValueFrom(
      this.http.get<PastaIAUsuarioResponse[]>(`${this.ChatIAEndpoint}PastasUsuario/${usuarioId}`, {
        headers: this.getHeader(this.loaderAtivo),
      })
    );
  }

  async obterMensagensDoChat(chatId: number): Promise<MensagemChatResponse[]> {
    return await firstValueFrom(
      this.http.get<MensagemChatResponse[]>(`${this.ChatIAEndpoint}MensagensChat/${chatId}`, {
        headers: this.getHeader(this.loaderAtivo),
      })
    );
  }

  async obterChatsSemPasta(usuarioId: number): Promise<ChatResponse[]> {
    return await firstValueFrom(
      this.http.get<ChatResponse[]>(`${this.ChatIAEndpoint}ChatSemPasta/${usuarioId}`, {
        headers: this.getHeader(this.loaderAtivo),
      })
    );
  }

  async obterChatsDaPasta(usuarioId: number, pastaId: number): Promise<ChatResponse[]> {
    return await firstValueFrom(
      this.http.get<ChatResponse[]>(`${this.ChatIAEndpoint}ObterChatsDaPasta/${usuarioId}/${pastaId}`, {
        headers: this.getHeader(this.loaderAtivo),
      })
    );
  }

  async obterTodosAgentes(): Promise<AgenteIAResponse[]> {
    return await firstValueFrom(
      this.http.get<AgenteIAResponse[]>(`${this.ChatIAEndpoint}AgentesIA`, {
        headers: this.getHeader(this.loaderAtivo),
      })
    );
  }

  async cadastrarChat(request: CadastrarChatRequest): Promise<number> {
    return await firstValueFrom(
      this.http.post<number>(`${this.ChatIAEndpoint}CadastrarChat`, request, {
        headers: this.getHeader(this.loaderAtivo),
      })
    );
  }

  async cadastrarPasta(request: CadastrarPastaRequest): Promise<number> {
    return await firstValueFrom(
      this.http.post<number>(`${this.ChatIAEndpoint}CadastrarPasta`, request, {
        headers: this.getHeader(this.loaderAtivo),
      })
    );
  }

  async excluirPasta(request: DeletarPastaRequest): Promise<boolean> {
    return await firstValueFrom(
      this.http.post<boolean>(`${this.ChatIAEndpoint}ExcluirPasta`, request, {
        headers: this.getHeader(this.loaderAtivo),
      })
    );
  }

  async surpreendaAgente(chatId: number, agenteId: number): Promise<string> {
    return await firstValueFrom(
      this.http.get<string>(`${this.ChatIAEndpoint}SurpreendaAgente/${chatId}/${agenteId}`, {
        headers: this.getHeader(this.loaderAtivo),
        responseType: 'text' as 'json'
      })
    );
  }

  async enviarMensagem(request: EnviarMensagemRequest): Promise<MensagemChatResponse> {
    return await firstValueFrom(
      this.http.post<MensagemChatResponse>(`${this.ChatIAEndpoint}EnviarMensagem`, request, {
        headers: this.getHeader(this.loaderAtivo),
      })
    );
  }

  async excluirChat(request: DeletarChatRequest): Promise<boolean> {
    return await firstValueFrom(
      this.http.post<boolean>(`${this.ChatIAEndpoint}ExcluirChat`, request, {
        headers: this.getHeader(this.loaderAtivo),
      })
    );
  }

  async editarPasta(request: EditarPastaRequest): Promise<PastaIAUsuarioResponse> {
    return await firstValueFrom(
      this.http.put<PastaIAUsuarioResponse>(`${this.ChatIAEndpoint}EditarPasta`, request, {
        headers: this.getHeader(this.loaderAtivo),
      })
    );
  }

  async renomearChat(request: RenomearChatRequest): Promise<ChatResponse> {
    return await firstValueFrom(
      this.http.put<ChatResponse>(`${this.ChatIAEndpoint}RenomearChat`, request, {
        headers: this.getHeader(this.loaderAtivo),
      })
    );
  }

  async moverChatPasta(request: MoverChatPastaRequest): Promise<void> {
    await firstValueFrom(
      this.http.put<void>(`${this.ChatIAEndpoint}MoverChatPasta`, request, {
        headers: this.getHeader(this.loaderAtivo),
      })
    );
  }
}
